interface Level {
  grid: {
    width: number,
    height: number,
    carpet: boolean[][],
  },
  objects: string[],
  positions: number[],
  hint: string,
  rules: { name: string, condition: (grid: string[][]) => boolean }[]
}

function posOf(g: string[][], item: string): [number, number] {
  for (let y = 0; y < g.length; y++) {
    for (let x = 0; x < g[0].length; x++) {
      if (g[y][x] == item) return [x, y];
    }
  }
  return [0, 0];
}
function diff(g: string[][], item1: string, item2: string): [number, number] {
  let p1 = posOf(g, item1);
  let p2 = posOf(g, item2);
  return [p2[0] - p1[0], p2[1] - p1[1]];
}
function dot_product(one: [number, number], two: [number, number]): number {
  return one[0] * two[0] + one[1] * two[1];
}
function dist(g: string[][], item1: string, item2: string): number {
  let pos1 = posOf(g, item1);
  let pos2 = posOf(g, item2);
  return Math.sqrt((pos1[0] - pos2[0]) ** 2 + (pos1[1] - pos2[1]) ** 2);
}
function angle(g: string[][], item1: string, item2: string, item3: string): number {
  let pos1 = posOf(g, item1);
  let pos2 = posOf(g, item2);
  let pos3 = posOf(g, item3);
  let item2_to_1 = [pos1[0] - pos2[0], pos1[1] - pos2[1]];
  let item2_to_3 = [pos3[0] - pos2[0], pos3[1] - pos2[1]];
  let dot_product = item2_to_1[0] * item2_to_3[0] + item2_to_1[1] * item2_to_3[1];
  let cosine = dot_product / Math.sqrt(item2_to_1[0] ** 2 + item2_to_1[1] ** 2) / Math.sqrt(item2_to_3[0] ** 2 + item2_to_3[1] ** 2);
  return Math.round(Math.acos(cosine) * 180 / Math.PI);
}
const hiding_spots: [number, number, string][] = [
  [192, 208, "above"], // 0: FLOOR ROW 1
  [208, 208, "above"], // 1: FLOOR ROW 2
  [224, 208, "above"], // 2: FLOOR ROW 3
  [240, 208, "above"], // 3: FLOOR ROW 4
  [128, 68, "above"], // 4: ON THE BOOKSHELF
  [320, 80, "above"], // 5: ON THE COUNTER
  [336, 120, "above"], // 6: ON THE ISLAND
  [327, 179, "above"], // 7: ON THE TABLE
  [352, 208, "above"], // 8: BOTTOM RIGHT CORNER
  [256, 38, "above"], // 9: ON THE CLOSET
  [122, 124, "above"], // 10: ON THE CAT TREE
  [224, 80, "above"], // 11: NEAR LAMP
  [110, 96, "under"], // 12: BEHIND BOOKSHELF
  [110, 208, "under"], // 13: BOTTOM LEFT CORNER
  [350, 68, "above"], // 14: ON FRIDGE
  [112, 172, "under"], // 15: NEXT TO COUCH
  [176, 96, "above"], // 16: FLOOR 1
  [160, 176, "above"], // 17: FLOOR 2
  [288, 160, "above"], // 18: FLOOR 3
]
const levels: Level[] = [
  // LEVEL 1
  {
    grid: {
      width: 4,
      height: 1,
      carpet: [[true, true, true, true]],
    },
    objects: ["pumpkin", "cat_food_dry", "water", "cat_food_wet"],
    positions: [0, 1, 2, 3],
    hint: "",
    rules: [
      {
        name: "NO WATER NEXT TO DRY FOOD",
        condition: (g: string[][]) => dist(g, "water", "cat_food_dry") > 1,
      },
      {
        name: "WATER ON THE LEFT",
        condition: (g: string[][]) => g[0][0] == "water"
      },
      {
        name: "NO WET FOOD NEXT TO PUMPKIN",
        condition: (g: string[][]) => dist(g, "cat_food_wet", "pumpkin") > 1,
      }
    ],
  },
  // LEVEL 2
  {
    grid: {
      width: 3,
      height: 2,
      carpet: [
        [true, true, true],
        [true, true, false],
      ],
    },
    objects: ["pumpkin", "cat_food_dry"],
    positions: [6, 7],
    hint: "",
    rules: [
      {
        name: "PUMPKIN FAR AWAY FROM FOOD",
        condition: (g: string[][]) => {
          return dist(g, "pumpkin", "cat_food_dry") > 2;
        },
      },
    ],
  },
  // LEVEL 3
  {
    grid: {
      width: 5,
      height: 4,
      carpet: [
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
      ],
    },
    objects: ["pumpkin", "water", "cat_food_dry"],
    positions: [14, 11, 13],
    hint: "A RIGHT TRIANGLE WITH SIDES 3 AND 4 WILL HAVE HYPOTENUSE 5",
    rules: [
      {
        name: "WATER AND FOOD EXACTLY 5 UNITS APART (MAKE A 3-4-5 TRIANGLE)",
        condition: (g: string[][]) => {
          return dist(g, "water", "cat_food_dry") == 5;
        },
      },
      {
        name: "MAKE IT A RIGHT TRIANGLE",
        condition: (g: string[][]) => {
          // Dot product of pumpkin to water vector and pumpkin to food vector should be 0
          console.log(angle(g, "water", "pumpkin", "cat_food_dry"));
          return angle(g, "water", "pumpkin", "cat_food_dry") == 90;
        },
      },
    ],
  },
  // LEVEL 4
  {
    grid: {
      width: 4,
      height: 4,
      carpet: [
        [true, true, false, true],
        [true, false, true, true],
        [true, true, true, false],
        [false, true, true, true],
      ]
    },
    objects: ["cat_food_wet", "water", "cat_food_dry"],
    positions: [15, 10, 5],
    hint: "",
    rules: [
      {
        name: "WET FOOD CLOSER TO DRY FOOD THAN TO WATER",
        condition: (g: string[][]) => {
          return dist(g, "cat_food_wet", "cat_food_dry") < dist(g, "cat_food_wet", "water");
        }
      },
      {
        name: "ALL 3 IN A STRAIGHT LINE",
        condition: (g: string[][]) => {
          let ang = angle(g, "cat_food_wet", "cat_food_dry", "water");
          return ang == 0 || ang == 180;
        }
      }
    ]
  },
  // LEVEL 5
  {
    grid: {
      width: 2,
      height: 3,
      carpet: [[true, true], [true, true], [true, true]],
    },
    objects: ["cat_food_dry", "water", "pumpkin"],
    positions: [12, 13, 16],
    hint: "WHEN HE SAYS ABOVE IT DOESNT HAVE TO BE DIRECTLY ABOVE",
    rules: [
      {
        name: "FOOD LEFT OF PUMPKIN",
        condition: (g: string[][]) => {
          return posOf(g, "cat_food_dry")[0] < posOf(g, "pumpkin")[0];
        }
      },
      {
        name: "WATER ABOVE FOOD",
        condition: (g: string[][]) => {
          return posOf(g, "water")[1] < posOf(g, "cat_food_dry")[1];
        }
      },
      {
        name: "PUMPKIN ABOVE WATER",
        condition: (g: string[][]) => {
          return posOf(g, "pumpkin")[1] < posOf(g, "water")[1];
        }
      }
    ]
  },
  // LEVEL 6
  {
    grid: {
      width: 3,
      height: 3,
      carpet: [
        [true, true, true],
        [true, true, true],
        [true, true, true],
      ]
    },
    objects: ["water", "water", "water", "water", "cat_food_dry", "cat_food_dry", "cat_food_dry", "cat_food_dry", "cat_food_dry"],
    positions: [1, 6, 14, 9, 10, 15, 12, 17, 8],
    hint: "YOURE GOING TO HAVE TO GET CHECKERED",
    rules: [
      {
        name: "NO WATER ADJACENT",
        condition: (g: string[][]) => {
          for (let x = 0; x < 3; x++) {
            for (let y = 0; y < 2; y++) {
              if (g[y][x] == "water" && g[y][x + 1] == "water") return false;
              if (g[y][x] == "water" && g[y + 1][x] == "water") return false;
            }
          }
          if (g[2][0] == "water" && g[2][1] == "water") return false;
          if (g[2][1] == "water" && g[2][2] == "water") return false;
          return true;
        }
      },
      {
        name: "NO FOOD ADJACENT",
        condition: (g: string[][]) => {
          for (let x = 0; x < 3; x++) {
            for (let y = 0; y < 2; y++) {
              if (g[y][x] == "cat_food_dry" && g[y][x + 1] == "cat_food_dry") return false;
              if (g[y][x] == "cat_food_dry" && g[y + 1][x] == "cat_food_dry") return false;
            }
          }
          if (g[2][0] == "cat_food_dry" && g[2][1] == "cat_food_dry") return false;
          if (g[2][1] == "cat_food_dry" && g[2][2] == "cat_food_dry") return false;
          return true;
        }
      }
    ]
  },
  // LEVEL 7
  {
    grid: {
      width: 5,
      height: 5,
      carpet: [
        [true, true, true, false, true],
        [true, false, true, true, true],
        [true, true, false, false, true],
        [true, false, true, true, false],
        [true, true, true, true, false],
      ]
    },
    objects: ["cat_food_wet", "cat_food_wet", "cat_food_wet", "cat_food_wet", "cat_food_wet"],
    positions: [13, 4, 5, 8, 9],
    hint: "PUT A CAN IN THE BOTTOM LEFT CORNER TO START",
    rules: [
      {
        name: "NO FOOD SHARING THE SAME ROW",
        condition: (g: string[][]) => {
          return g.every(r => r.filter(v => v != "").length == 1);
        }
      },
      {
        name: "NO FOOD SHARING THE SAME COLUMN",
        condition: (g: string[][]) => {
          for (let x = 0; x < 5; x++) {
            if ([0, 1, 2, 3, 4].filter(i => g[i][x] != "").length != 1)
              return false;
          }
          return true;
        }
      },
      {
        name: "NO FOOD DIAGONALLY ADJACENT",
        condition: (g: string[][]) => {
          for (let x = 1; x < 4; x++) {
            for (let y = 1; y < 4; y++) {
              if (g[y][x] != "") {
                if (g[y + 1][x + 1] == "cat_food_wet") return false;
                if (g[y + 1][x - 1] == "cat_food_wet") return false;
                if (g[y - 1][x + 1] == "cat_food_wet") return false;
                if (g[y - 1][x - 1] == "cat_food_wet") return false;
              }
            }
          }
          return true;
        }
      }
    ]
  },
  // LEVEL 8
  {
    grid: {
      width: 3,
      height: 2,
      carpet: [
        [true, true, true],
        [true, true, true],
      ]
    },
    objects: ["pumpkin.1", "pumpkin.2", "water"],
    positions: [10, 13, 6],
    hint: "THREE POINTS MAKE A TRIANGLE",
    rules: [
      {
        name: "MAKE A TRIANGLE WITH THREE DIFFERENT SIDE LENGTHS",
        condition: (g: string[][]) => {
          let d1 = dist(g, "pumpkin.1", "pumpkin.2");
          let d2 = dist(g, "pumpkin.1", "water");
          let d3 = dist(g, "pumpkin.2", "water");
          return d1 != d2 && d2 != d3 && d1 != d3;
        }
      },
      {
        name: "NO RIGHT ANGLES",
        condition: (g: string[][]) => {
          let a1 = angle(g, "pumpkin.1", "pumpkin.2", "water");
          let a2 = angle(g, "pumpkin.1", "water", "pumpkin.2");
          let a3 = angle(g, "pumpkin.2", "pumpkin.1", "water");
          return a1 != 90 && a2 != 90 && a3 != 90;
        }
      },
      {
        name: "THE WATER SHOULD HAVE ANGLE ABOVE 90",
        condition: (g: string[][]) => {
          return angle(g, "pumpkin.1", "water", "pumpkin.2") > 90;
        }
      }
    ]
  },
  // LEVEL 9
  {
    grid: {
      width: 5,
      height: 4,
      carpet: [
        [true, true, false, true, true],
        [true, true, true, true, true],
        [false, true, true, true, true],
        [true, true, true, true, true],
      ]
    },
    objects: ["pumpkin", "cat_food_wet", "water"],
    positions: [7, 5, 8],
    hint: "START WITH A STRAIGHT LINE WITH SLOPE -0.5",
    rules: [
      {
        name: "PUMPKIN AS CLOSE TO BED AS POSSIBLE",
        condition: (g: string[][]) => {
          let pump = posOf(g, "pumpkin");
          return pump[0] == 0 && pump[1] == 0;
        }
      },
      {
        name: "WATER EXACTLY TWICE AS FAR AWAY FROM PUMPKIN AS FOOD",
        condition: (g: string[][]) => {
          return Math.abs(dist(g, "pumpkin", "water") - 2 * dist(g, "pumpkin", "cat_food_wet")) < 0.01;
        }
      },
      {
        name: "NOT IN A STRAIGHT LINE",
        condition: (g: string[][]) => {
          let ang = angle(g, "pumpkin", "cat_food_wet", "water");
          return ang != 180 && ang != 0;
        }
      }
    ]
  },
  // LEVEL 10
  {
    grid: {
      width: 3,
      height: 3,
      carpet: [
        [true, false, true],
        [true, true, true],
        [true, true, false],
      ]
    },
    objects: ["cat_food_dry.1", "water.1", "cat_food_dry.2", "water.2"],
    positions: [11, 12, 9, 1],
    hint: "IDK HOW TO HELP YOU HERE",
    rules: [
      {
        name: "WATER NOT NEAR WATER",
        condition: (g: string[][]) => {
          return dist(g, "water.1", "water.2") > 1.5;
        }
      },
      {
        name: "FOOD NOT NEAR FOOD",
        condition: (g: string[][]) => {
          return dist(g, "cat_food_dry.1", "cat_food_dry.2") > 1.5;
        }
      },
      {
        name: "FOOD LINE AND WATER LINE ARE PARALLEL",
        condition: (g: string[][]) => {
          let dry1 = posOf(g, "cat_food_dry.1");
          let dry2 = posOf(g, "cat_food_dry.2");
          let water1 = posOf(g, "water.1");
          let water2 = posOf(g, "water.2");
          let dry_line = [dry2[0] - dry1[0], dry2[1] - dry1[1]];
          let water_line = [water2[0] - water1[0], water2[1] - water1[1]];
          return dry_line[0] * water_line[1] - dry_line[1] * water_line[0] == 0;
        }
      }
    ],
  },
  // LEVEL 11
  {
    grid: {
      width: 5,
      height: 5,
      carpet: [
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
      ]
    },
    objects: ["pumpkin", "water", "cat_food_dry", "cat_food_wet", "water.2"],
    positions: [18, 4, 7, 10, 12],
    hint: "REMEMBER THE TRIANGLE BACK IN LEVEL 3? DO THAT",
    rules: [
      {
        name: "WATER NEAR PUMPKIN",
        condition: (g: string[][]) => {
          return dist(g, "pumpkin", "water") < 1.5 && dist(g, "pumpkin", "water.2") < 1.5;
        }
      },
      {
        name: "WATER NOT NEAR DRY FOOD",
        condition: (g: string[][]) => {
          return dist(g, "water", "cat_food_dry") > 1.5 && dist(g, "water.2", "cat_food_dry") > 1.5;
        }
      },
      {
        name: "PUMPKIN NEAR DRY FOOD",
        condition: (g: string[][]) => {
          return dist(g, "pumpkin", "cat_food_dry") < 1.5;
        }
      },
      {
        name: "WET FOOD RIGHT OF PUMPKIN",
        condition: (g: string[][]) => {
          return posOf(g, "cat_food_wet")[0] > posOf(g, "pumpkin")[0]
        }
      },
      {
        name: "PUMPKIN ABOVE WET FOOD",
        condition: (g: string[][]) => {
          return posOf(g, "cat_food_wet")[1] > posOf(g, "pumpkin")[1]
        }
      },
      {
        name: "WET FOOD EXACTLY 5 UNITS FROM DRY FOOD (3-4-5 TRIANGLE)",
        condition: (g: string[][]) => {
          return dist(g, "cat_food_wet", "cat_food_dry") == 5;
        }
      },
    ]
  },
  // LEVEL 12
  {
    grid: {
      width: 4,
      height: 4,
      carpet: [
        [true, true, false, true],
        [true, true, true, true],
        [true, true, true, false],
        [true, false, true, true],
      ]
    },
    objects: ["water", "water.2", "cat_food_dry", "pumpkin", "pumpkin.2"],
    positions: [16, 9, 12, 13, 10],
    hint: "THIS ONE IS TRICKY",
    rules: [
      {
        name: "FOOD DIRECTLY IN BETWEEN WATER",
        condition: (g: string[][]) => {
          return angle(g, "water", "cat_food_dry", "water.2") == 180;
        }
      },
      {
        name: "PUMPKIN LINE PERPENDICULAR TO WATER LINE",
        condition: (g: string[][]) => {
          return dot_product(diff(g, "water", "water.2"), diff(g, "pumpkin", "pumpkin.2")) == 0;
        }
      },
      {
        name: "FOOD NOT IN LINE WITH PUMPKINS",
        condition: (g: string[][]) => {
          return angle(g, "pumpkin", "cat_food_dry", "pumpkin.2") % 180 != 0;
        }
      },
      {
        name: "WATER NOT IN LINE WITH PUMPKINS",
        condition: (g: string[][]) => {
          return angle(g, "pumpkin", "water", "pumpkin.2") % 180 != 0 && angle(g, "pumpkin", "water.2", "pumpkin.2") % 180 != 0;
        }
      },
      {
        name: "NO PUMPKINS NEAR WATER",
        condition: (g: string[][]) => {
          return dist(g, "pumpkin", "water") > 1.5 && dist(g, "pumpkin.2", "water") > 1.5 && dist(g, "pumpkin", "water.2") > 1.5 && dist(g, "pumpkin.2", "water.2") > 1.5;
        }
      }
    ]
  }
];

export { levels, hiding_spots, type Level }
